'use client'

/**
 * SearchLocation
 *
 * Client-side location input used by provider search pages. Responsibilities:
 * - Provide a single controlled input for ZIP, neighborhood, or city text.
 * - Debounce user input and geocode US ZIP codes (via `geocodeZip`) when the
 *   Google Maps JS API is available.
 * - Expose `onLocationChange(coords, text)` so parent wrappers can apply
 *   Algolia `aroundLatLng` via `Configure` when appropriate.
 * - Support browser geolocation ("Use my current location") with graceful
 *   degradation if permission is denied or the API times out.
 * - Avoid duplicate geocode requests for the same ZIP and show a loading
 *   spinner while a geocode request is in-flight.
 *
 * Keeping this as a focused client component keeps location concerns local
 * to the search UI and avoids introducing global stores or cross-cutting
 * side-effects. The component intentionally does not apply Algolia
 * configuration itself — it reports coordinates to the parent which may
 * choose how to apply them (page wrapper vs local Configure).
 */

import { useState, type SyntheticEvent, useEffect, useRef } from 'react'
import { Autocomplete, Icon, Typography } from '@shc/ui'
import { geocodeZip, getBrowserLocation, geocodeByCoordinates, type Coordinates } from '@lib/utilities/location'
import { useGoogleApiLoader } from '@hooks/use-google-api-loader'
import debounce from 'lodash/debounce'

type SearchLocationProps = {
  onLocationChange?: (coords: Coordinates | null, locationText: string) => void
}

const SearchLocation = ({ onLocationChange }: SearchLocationProps) => {
  const [queryValue, setQueryValue] = useState('')
  const [isGeocoding, setIsGeocoding] = useState(false)
  const [isGettingLocation, setIsGettingLocation] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { isLoaded } = useGoogleApiLoader()
  const lastGeocodedZipRef = useRef<string | null>(null)
  const latestQueryRef = useRef('')
  const debouncedGeocodeRef = useRef<ReturnType<typeof debounce> | null>(null)
  const initialValue = typeof globalThis.window === 'undefined' ? null : new URL(globalThis.window.location.href).searchParams.get('l');

  /**
   * Debounced geocoding helper
   *
   * Purpose:
   * - Debounce user input so we don't run a geocode request on every keystroke.
   * - Recognize ZIP codes (US 5 or 9 digit) and attempt to geocode them when
   *   the Google Maps API is available.
   * - Avoid duplicate network requests for the same ZIP by checking
   *   `lastGeocodedZipRef`.
   * - During geocoding `isGeocoding` is set so the UI can show a spinner.
   * - Non-ZIP input is passed through as text-only location state.
   */
  useEffect(() => {
    const debouncedGeocode = debounce(async (value: string) => {
      latestQueryRef.current = value

      if (!value.trim()) {
        lastGeocodedZipRef.current = null
        setIsGeocoding(false)
        onLocationChange?.(null, '')
        return
      }

      // Check if it looks like a ZIP code (5 digits)
      const zipMatch = /^\d{5}(-\d{4})?$/.exec(value.trim())
      if (zipMatch) {
        const zipClean = zipMatch[0]
        // avoid duplicate geocode calls for the same ZIP
        if (lastGeocodedZipRef.current === zipClean) return

        setIsGeocoding(true)
        const coords = isLoaded
          ? await geocodeZip(value)
          : null
        if (!isLoaded) {
          // Google API not loaded yet — geocodeZip requires the API to be loaded,
          // so we return null and let the UI remain in text-only mode.
          // A retry is attempted once the API becomes available.
        }
        setIsGeocoding(false)

        if (latestQueryRef.current.trim() !== value.trim()) {
          return
        }

        if (coords) {
          lastGeocodedZipRef.current = zipClean
          onLocationChange?.(coords, coords.label ?? value)
        } else {
          // Geocoding failed, clear location and allow future retries
          lastGeocodedZipRef.current = null
          onLocationChange?.(null, '')
          setError('Please enter a valid U.S. ZIP code.')
        }
      } else {
        // Not a recognized format, just store the text
        onLocationChange?.(null, value)
        if(value !== 'Current location'){
          setError('Please enter a valid U.S. ZIP code.')
        }
      }
    }, 200)

    debouncedGeocodeRef.current = debouncedGeocode

    return () => {
      debouncedGeocode.cancel()
    }
  
  }, [onLocationChange, isLoaded])

  const handleQueryValueChange = async (event: SyntheticEvent<Element, Event>|null, value: string, reason: string) => {
    setError(null);

    if(reason === "selectOption"){
      return await handleUseMyLocation();
    }
    setQueryValue(value)

    if (!value.trim()) {
      latestQueryRef.current = ''
      lastGeocodedZipRef.current = null
      setIsGeocoding(false)
      debouncedGeocodeRef.current?.cancel()
      onLocationChange?.(null, '')
      return
    }

    debouncedGeocodeRef.current?.(value)
  }

  const handleUseMyLocation = async () => {
    setIsGettingLocation(true)
    const coords = await getBrowserLocation()
    setIsGettingLocation(false)

    if (coords) {
      const myLoc = await geocodeByCoordinates({ ...coords })
      setQueryValue('Current location')
      onLocationChange?.(coords, myLoc?.formattedAddress ?? 'Current location')
    } else {
      // Could show error message
      setError('Enable location permissions to continue.')
      console.warn('Unable to get browser location', coords)
    }
  }

  // delay the initial value geocoding so we don't get canceled by the debounce
  useEffect(() => {
    if(isLoaded && initialValue){
      const debounceStart = debounce(() => {
        setQueryValue(initialValue)
      }, 250);
      debounceStart();
    }
  },[isLoaded])

  return (
    <div className="col-span-12 md:col-span-6 xl:col-span-4">
      <Autocomplete
        className='input-reset'
        data-testid={'location-search'}
        freeSolo
        value={queryValue}
        placeholder="ZIP code"
        options={[
          {
            label: 'Current location',
            value: 'Current location'
          }
        ]}
        disabled={isGettingLocation}
        error={error ? (<Typography className="fad-error-text"><Icon icon="triangle-exclamation" /> {error}</Typography>) : undefined}
        onInputChange={handleQueryValueChange}
        startDecorator={<Icon className="h-3.5" icon="location-dot" />}
        endDecorator={isGeocoding && <Icon icon="spinner-third" spin />}
        filterOptions={(o) => o}
        renderOption={(o: any) => (
            <div className="flex items-center gap-2">
              <Icon icon="location-arrow" className="text-primary" />
              <Typography variant="body-semibold">{o.label}</Typography>
            </div>
          )
      }
      />
    </div>
  )
}

export default SearchLocation